import { Tooltip } from 'antd'

export interface ProductPillProps {
  /** The product name to display */
  name: string | null | undefined
  /** The amount/quantity to display in the dark section */
  amount?: number
  /** The volume for the tooltip (in gallons) */
  volume?: number
}

/**
 * Renders a product as a pill with amount on left (dark) and name on right (light).
 * Matches the styling from bb_frontend's OrderProducts component.
 *
 * The pill never draws wider than whatever contains it: the amount keeps its
 * full width and the name gives way with an ellipsis. Without that the pill
 * overflows its grid cell and gets clipped mid-character, losing its right
 * border with it.
 */
export function ProductPill({ name, amount, volume }: ProductPillProps) {
  const label = name || 'Unknown'
  // The name is the half that truncates, so the tooltip carries it in full
  // whether or not there's also a volume to report.
  const tooltipTitle = volume
    ? `${label} — ${volume.toLocaleString()} gal`
    : label

  const pill = (
    <div style={{ display: 'inline-flex', maxWidth: '100%', minWidth: 0 }}>
      {/* Explicitly null rather than `amount && ...`, which would render a
          bare 0 for a zero amount. */}
      {amount ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: 18,
            padding: '2px 6px',
            background: 'var(--gray-700, #374151)',
            borderRadius: '6px 0 0 6px',
            border: 'solid 1px var(--gray-700, #374151)',
            color: 'var(--gray-100, #f3f4f6)',
            fontWeight: 'bold',
            fontSize: 11,
            flexShrink: 0,
          }}
        >
          {amount}
        </div>
      ) : null}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '2px 6px',
          fontWeight: 600,
          fontSize: 11,
          textTransform: 'uppercase',
          background: 'var(--bg-2, #f9fafb)',
          borderRadius: amount ? '0 6px 6px 0' : '6px',
          border: 'solid 1px var(--gray-300, #d1d5db)',
          whiteSpace: 'nowrap',
          // A flex item only shrinks past its content once its automatic
          // minimum size is out of the way.
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      </div>
    </div>
  )

  return (
    <Tooltip placement='top' title={tooltipTitle}>
      {pill}
    </Tooltip>
  )
}
